import {
  Bot,
  Brain,
  MessageSquare,
  Shield,
  Zap,
  Calendar,
  Search,
  FileText,
  Mail,
  Users,
  Settings,
  Lock,
  Cloud,
  Sparkles,
  Target,
  Lightbulb
} from 'lucide-react'

export interface Capability {
  id: string
  title: string
  description: string
  icon: typeof Bot
  color: string
}

export interface Feature {
  id: string
  title: string
  description: string
  icon: typeof Bot
  benefits: string[]
}

export const capabilities: Capability[] = [
  {
    id: 'task-management',
    title: 'Task Management',
    description: 'Organize, prioritize, and track your tasks with intelligent automation',
    icon: Calendar,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'research',
    title: 'Research Assistant',
    description: 'Deep research capabilities across multiple sources with summarization',
    icon: Search,
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'communication',
    title: 'Communication Helper',
    description: 'Draft emails, messages, and documents in your personal style',
    icon: Mail,
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'document-analysis',
    title: 'Document Analysis',
    description: 'Extract insights and summarize complex documents quickly',
    icon: FileText,
    color: 'from-orange-500 to-amber-500'
  },
  {
    id: 'scheduling',
    title: 'Smart Scheduling',
    description: 'Intelligent calendar management that learns your preferences',
    icon: Calendar,
    color: 'from-red-500 to-rose-500'
  },
  {
    id: 'collaboration',
    title: 'Team Collaboration',
    description: 'Seamless integration with team workflows and communication',
    icon: Users,
    color: 'from-indigo-500 to-violet-500'
  }
]

export const features: Feature[] = [
  {
    id: 'personalization',
    title: 'Deep Personalization',
    description: 'Your AI Twin learns your communication style, preferences, and work patterns over time.',
    icon: Brain,
    benefits: [
      'Adapts to your writing style',
      'Learns your scheduling preferences',
      'Understands your priorities',
      'Remembers context from past interactions'
    ]
  },
  {
    id: 'privacy',
    title: 'Privacy First',
    description: 'Your data stays yours. We use state-of-the-art encryption and local processing.',
    icon: Shield,
    benefits: [
      'End-to-end encryption',
      'Local data processing option',
      'No data selling or sharing',
      'Full data export and deletion'
    ]
  },
  {
    id: 'integration',
    title: 'Seamless Integration',
    description: 'Connect with your existing tools and workflows without friction.',
    icon: Zap,
    benefits: [
      'Works with popular apps',
      'API access for developers',
      'Browser extension available',
      'Mobile apps for iOS and Android'
    ]
  },
  {
    id: 'intelligence',
    title: 'Advanced Intelligence',
    description: 'Powered by cutting-edge AI models fine-tuned for personal assistance.',
    icon: Sparkles,
    benefits: [
      'Context-aware responses',
      'Multi-step task completion',
      'Natural language understanding',
      'Continuous learning and improvement'
    ]
  }
]

export const privacyFeatures = [
  {
    id: 'encryption',
    title: 'End-to-End Encryption',
    description: 'All your data is encrypted in transit and at rest using AES-256',
    icon: Lock
  },
  {
    id: 'local-processing',
    title: 'Local Processing Option',
    description: 'Process sensitive data locally on your device when needed',
    icon: Settings
  },
  {
    id: 'data-control',
    title: 'Full Data Control',
    description: 'Export or delete your data anytime with one click',
    icon: Cloud
  },
  {
    id: 'no-training',
    title: 'No Model Training',
    description: 'Your data is never used to train our AI models',
    icon: Shield
  }
]

export const useCases = [
  {
    id: 'professional',
    title: 'Professional',
    description: 'Boost your productivity with intelligent task management and communication assistance',
    icon: Target,
    examples: [
      'Draft professional emails in your style',
      'Prepare meeting summaries and action items',
      'Research industry trends and competitors',
      'Manage complex project timelines'
    ]
  },
  {
    id: 'creative',
    title: 'Creative',
    description: 'Unlock your creative potential with brainstorming and content creation support',
    icon: Lightbulb,
    examples: [
      'Brainstorm ideas for projects',
      'Get feedback on creative work',
      'Generate content outlines',
      'Find inspiration and references'
    ]
  },
  {
    id: 'personal',
    title: 'Personal',
    description: 'Simplify your daily life with a personalized assistant who knows you',
    icon: MessageSquare,
    examples: [
      'Plan trips and manage itineraries',
      'Track personal goals and habits',
      'Get recommendations tailored to you',
      'Organize personal information'
    ]
  }
]

export const stepData = [
  {
    id: 1,
    title: 'Your AI Twin',
    subtitle: 'A digital assistant that knows you',
    description: 'AssistantTwin is your personalized AI companion that learns your unique communication style, preferences, and work patterns to provide truly personalized assistance.',
    icon: Bot
  },
  {
    id: 2,
    title: 'Personalization',
    subtitle: 'Learning your preferences',
    description: 'Through natural interactions, your AI Twin adapts to understand how you work, communicate, and make decisions, becoming more helpful over time.',
    icon: Brain
  },
  {
    id: 3,
    title: 'Capabilities',
    subtitle: 'Tasks, research, communication',
    description: 'From managing your schedule to researching topics and drafting communications, your AI Twin handles a wide range of tasks with intelligence and care.',
    icon: Zap
  },
  {
    id: 4,
    title: 'Privacy First',
    subtitle: 'Your data stays yours',
    description: 'We believe your personal data should remain personal. AssistantTwin uses end-to-end encryption and gives you full control over your information.',
    icon: Shield
  },
  {
    id: 5,
    title: 'Interactive Demo',
    subtitle: 'See it in action',
    description: 'Experience how AssistantTwin works through an interactive demonstration. See real examples of how your AI Twin can assist with daily tasks.',
    icon: MessageSquare
  },
  {
    id: 6,
    title: 'Getting Started',
    subtitle: 'Begin your AI journey',
    description: 'Ready to meet your AI Twin? Start your journey with AssistantTwin today and discover a new way to enhance your productivity and creativity.',
    icon: Sparkles
  }
]
